import queryClient from '../../../ReactQuery/client';
import useMutation from '../../../ReactQuery/useMutation';
import { put } from '../../../redux/api';
import type { TeamWithAssociations, UpdateTeamRequest } from '../../../../shared/types/routes/teams';

import { getTeamsQueryKey } from './useTeams';

//  Function to update a team
function updateTeam(team: UpdateTeamRequest) {
  return put<UpdateTeamRequest, TeamWithAssociations>(`/api/teams/${team.id}`, team)
    //  Note: we do this because we don't reject 400s in our Redux API helpers.
    .then((resp) => {
      if (resp.status >= 400) {
        throw new Error(`Error while updating team ${team.id}.`);
      }

      return resp?.data ?? null;
    });
}

/**
 * useUpdateTeam is a hook that updates a team and invalidates the teams query.
 */
export default function useUpdateTeam(team: UpdateTeamRequest) {
  return useMutation({
    mutationFn: () => updateTeam(team),
    onSuccess: () => {
      // First we add the new team to the cache, so that it appears immediately.
      queryClient.setQueryData(getTeamsQueryKey, (oldTeams: TeamWithAssociations[] | undefined) => {
        if (!oldTeams) {
          return undefined;
        }
        return oldTeams.map((oldTeam: TeamWithAssociations) => oldTeam.id !== team.id ? oldTeam : ({
          ...oldTeam,
          ...team,
        }));
      });
      // Then we invalidate the teams query, so that the new full team data is fetched.
      void queryClient.invalidateQueries(getTeamsQueryKey);
    },
  });
}
